import { useEffect } from 'react';
import type { JSX } from 'react';
import { WorkspaceProvider } from './WorkspaceProvider';
import { useWorkspace } from './workspace-context';
import { useNavigation } from './navigation';
import { AppShell } from '../layouts/AppShell';
import { OverviewPage } from '../features/dashboard/OverviewPage';
import { PropertyPage } from '../features/property/PropertyPage';
import { BuyerHotelsPage } from '../features/property/BuyerHotelsPage';
import { InventoryPage } from '../features/inventory/InventoryPage';
import { PartnersPage } from '../features/partners/PartnersPage';
import { BuyerAgreementsPage } from '../features/partners/BuyerAgreementsPage';
import { ReservationsPage } from '../features/reservations/ReservationsPage';
import { SearchPage } from '../features/reservations/SearchPage';
import { DistributionPage } from '../features/distribution/DistributionPage';
import { ReportsPage } from '../features/reports/ReportsPage';
import { SettingsPage } from '../features/settings/SettingsPage';
import { AppLink, Button, EmptyState, Notice, Panel } from '../shared/ui/primitives';
import { Icon } from '../shared/ui/Icon';
import { workspaceMode } from './navigation';

const screens: Record<string, () => JSX.Element> = {
    '/': OverviewPage,
    '/overview': OverviewPage,
    '/property': PropertyPage,
    '/inventory': InventoryPage,
    '/partners': PartnersPage,
    '/reservations': ReservationsPage,
    '/search': SearchPage,
    '/distribution': DistributionPage,
    '/reports': ReportsPage,
    '/settings': SettingsPage,
};

const buyerScreens: Record<string, () => JSX.Element> = {
    '/': SearchPage,
    '/overview': SearchPage,
    '/property': BuyerHotelsPage,
    '/partners': BuyerAgreementsPage,
    '/search': SearchPage,
    '/reservations': ReservationsPage,
    '/settings': SettingsPage,
};

function WorkspaceRoutes() {
    const { data, error, loading, refresh } = useWorkspace();
    const { path, params, href } = useNavigation();
    const buyer = workspaceMode(params) === 'buyer';
    useEffect(() => { window.scrollTo({ top: 0 }); }, [path]);
    if (!data) return <div className="workspace-start"><div className="start-brand"><span className="brand-mark"><Icon name="sun" size={27} /></span><strong>dexa</strong><BadgeLabel /></div><Panel><div className="padded">
        {loading ? <div className="startup-loading" role="status"><span className="spinner" /><h1>Preparing your workspace</h1><p>Loading your properties, calendar and reservations.</p></div> : <><h1>Connect the local workspace</h1><p>The React application is ready. Start the Go API with the demo workspace enabled.</p><Notice tone="warning">{error || 'The local demo is not enabled.'}</Notice><pre className="setup-code">{`# Run from backend/\nHOTEL_DEMO=true \\\nHOTEL_DATA_PATH=data/hotel-demo.json \\\ngo run ./cmd/server`}</pre><p className="field-hint">PowerShell: set $env:HOTEL_DEMO="true" and $env:HOTEL_DATA_PATH="data/hotel-demo.json" before running the server. The dev Compose configuration enables these for you.</p><Button onClick={() => { void refresh().catch(() => undefined); }}><Icon name="refresh" size={17} />Retry connection</Button></>}
    </div></Panel><p className="startup-note">Fictional sample data only · no real hotel bookings · no payment processing</p></div>;
    const Screen = (buyer ? buyerScreens : screens)[path];
    const scope = `${path}:${params.get('workspace') ?? 'seller'}:${params.get('property') ?? data.properties[0]?.id}:${params.get('amend') ?? ''}`;
    return <AppShell>{Screen ? <Screen key={scope} /> : <Panel><EmptyState title={buyer ? 'Seller-only workspace area' : "This page doesn't exist"} description={buyer ? 'Switch to Seller workspace to manage properties, inventory, rates and distribution.' : 'Choose a workspace section from the sidebar.'} action={<AppLink href={href(buyer ? '/search' : '/overview')} className="button button-primary">{buyer ? 'Back to buyer workspace' : 'Back to overview'}</AppLink>} /></Panel>}</AppShell>;
}
function BadgeLabel() { return <span className="start-label">ARUBA HOTEL WORKSPACE</span>; }
export function AppRouter() { return <WorkspaceProvider><WorkspaceRoutes /></WorkspaceProvider>; }
